import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { PublicKey, Transaction } from '@solana/web3.js';
import { createTransferCheckedInstruction, getAssociatedTokenAddress, createAssociatedTokenAccountIdempotentInstruction, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { apiFetch, getApiHost } from '../utils/api';

const USDC_MINT = new PublicKey('EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v');
const USDC_DECIMALS = 6;

type PaymentMethod = 'card' | 'usdc';

interface SpotlightOption {
  tier: 'regular' | 'sponsor';
  durationHours: 12 | 24;
  price: string;
  priceUsdc: number;
  label: string;
  description: string;
}

const OPTIONS: SpotlightOption[] = [
  {
    tier: 'regular',
    durationHours: 12,
    price: '$99',
    priceUsdc: 99,
    label: 'Spotlight 12hr',
    description: 'Featured at the top of the watchlist for 12 hours',
  },
  {
    tier: 'regular',
    durationHours: 24,
    price: '$179',
    priceUsdc: 179,
    label: 'Spotlight 24hr',
    description: 'Featured at the top of the watchlist for 24 hours',
  },
  {
    tier: 'sponsor',
    durationHours: 12,
    price: '$299',
    priceUsdc: 299,
    label: 'Research Sponsor 12hr',
    description: 'Top placement + auto social research on all new watchlist tokens',
  },
  {
    tier: 'sponsor',
    durationHours: 24,
    price: '$549',
    priceUsdc: 549,
    label: 'Research Sponsor 24hr',
    description: 'Top placement + auto social research on all new watchlist tokens',
  },
];

interface Availability {
  tier: string;
  maxSlots: number;
  slotsAvailable: number;
  nextAvailableAt: string;
}

interface ValidatedToken {
  mint: string;
  name: string;
  symbol: string;
  image: string;
  description: string;
  safetyLevel: string;
  market_cap: number;
  holders: number;
  watchlistAddedAt: string;
}

interface SpotlightPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedMint?: string;
  preselectedName?: string;
  preselectedSymbol?: string;
}

const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMinutes > 0) return `${diffMinutes}m ago`;
  return 'now';
};

const SpotlightPreviewCard: React.FC<{
  token: ValidatedToken;
  isSponsor: boolean;
  formatTimeAgo: (d: string) => string;
  formatMarketCap: (mc: number) => string;
}> = ({ token, isSponsor, formatTimeAgo, formatMarketCap }) => (
  <div>
    <p className="text-gray-500 text-[11px] mb-2">Preview — how it will appear on the watchlist:</p>
    <div className={`rounded-xl p-4 flex items-center gap-3 ${
      isSponsor
        ? 'bg-gradient-to-r from-purple-900/20 to-crypto-gray border border-purple-500/30'
        : 'bg-crypto-gray border border-crypto-yellow/20'
    }`}>
      {token.image ? (
        <img src={token.image} alt="" className="w-10 h-10 rounded-full object-cover bg-gray-700" />
      ) : (
        <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-gray-400 font-bold text-lg">
          {token.symbol.charAt(0)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="font-semibold text-[15px] text-white">{token.symbol}</span>
            <span className="text-gray-400 text-[13px] truncate">{token.name}</span>
          </div>
          {isSponsor ? (
            <span className="text-[10px] font-semibold text-purple-300 bg-purple-500/20 px-2 py-0.5 rounded-full flex items-center gap-1 ml-2 whitespace-nowrap">
              <i className="fas fa-crown text-[8px]"></i>
              SPONSOR
            </span>
          ) : (
            <span className="text-[10px] font-semibold text-crypto-yellow bg-crypto-yellow/10 px-2 py-0.5 rounded-full flex items-center gap-1 ml-2 whitespace-nowrap">
              <i className="fas fa-star text-[8px]"></i>
              SPOTLIGHT
            </span>
          )}
        </div>
        <div className="flex items-center justify-between text-[12px]">
          <span className="text-gray-500">Added {formatTimeAgo(token.watchlistAddedAt)}</span>
          {token.market_cap > 0 && (
            <span className="text-gray-500">{formatMarketCap(token.market_cap)}</span>
          )}
        </div>
      </div>
    </div>
    {token.description && (
      <p className="text-gray-500 text-xs mt-2 line-clamp-2">{token.description}</p>
    )}
  </div>
);

const SpotlightPurchaseModal: React.FC<SpotlightPurchaseModalProps> = ({
  isOpen,
  onClose,
  preselectedMint,
  preselectedName,
  preselectedSymbol,
}) => {
  const { publicKey, signTransaction, connected } = useWallet();
  const { setVisible: setWalletModalVisible } = useWalletModal();

  const [mintInput, setMintInput] = useState(preselectedMint || '');
  const [selectedOption, setSelectedOption] = useState<SpotlightOption | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('usdc');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [usdcStatus, setUsdcStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [usdcSuccess, setUsdcSuccess] = useState<{ txSignature: string } | null>(null);
  const [regularAvailability, setRegularAvailability] = useState<Availability | null>(null);
  const [sponsorAvailability, setSponsorAvailability] = useState<Availability | null>(null);

  // Token validation state
  const [validatedToken, setValidatedToken] = useState<ValidatedToken | null>(null);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (preselectedMint) setMintInput(preselectedMint);
  }, [preselectedMint]);

  useEffect(() => {
    if (!isOpen) return;
    Promise.all([
      apiFetch(`${getApiHost()}/api/spotlight/availability?tier=regular`).then(r => r.json()),
      apiFetch(`${getApiHost()}/api/spotlight/availability?tier=sponsor`).then(r => r.json()),
    ]).then(([regular, sponsor]) => {
      setRegularAvailability(regular);
      setSponsorAvailability(sponsor);
    }).catch(() => {});
  }, [isOpen]);

  // Validate mint when input changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const mint = mintInput.trim();
    if (!mint || mint.length < 32) {
      setValidatedToken(null);
      setValidationError(null);
      return;
    }

    setValidating(true);
    setValidationError(null);

    debounceRef.current = setTimeout(async () => {
      try {
        const response = await apiFetch(`${getApiHost()}/api/spotlight/validate/${mint}`);
        const data = await response.json();

        if (data.eligible) {
          setValidatedToken(data.token);
          setValidationError(null);
        } else {
          setValidatedToken(null);
          setValidationError(data.error || 'Token is not eligible');
        }
      } catch {
        setValidatedToken(null);
        setValidationError('Failed to validate token');
      } finally {
        setValidating(false);
      }
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [mintInput]);

  const handlePurchase = async () => {
    if (!validatedToken || !selectedOption) return;

    if (paymentMethod === 'usdc') {
      await handleUsdcPurchase();
    } else {
      await handleStripePurchase();
    }
  };

  const handleStripePurchase = async () => {
    if (!validatedToken || !selectedOption) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(`${getApiHost()}/api/spotlight/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mint: validatedToken.mint,
          tier: selectedOption.tier,
          durationHours: selectedOption.durationHours,
          email: email || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to create checkout');
        return;
      }

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUsdcPurchase = async () => {
    if (!validatedToken || !selectedOption) return;

    // Prompt wallet connection if needed
    if (!publicKey || !signTransaction) {
      setWalletModalVisible(true);
      return;
    }

    setLoading(true);
    setError(null);
    setUsdcSuccess(null);
    setUsdcStatus('Creating purchase...');

    try {
      // Step 1: Create the spotlight record and get payment details
      const purchaseRes = await apiFetch(`${getApiHost()}/api/spotlight/purchase-usdc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mint: validatedToken.mint,
          tier: selectedOption.tier,
          durationHours: selectedOption.durationHours,
          walletAddress: publicKey.toBase58(),
        }),
      });

      const purchaseData = await purchaseRes.json();
      if (!purchaseRes.ok) {
        setError(purchaseData.error || 'Failed to create purchase');
        return;
      }

      const { spotlightId, amountUsdc, treasuryWallet } = purchaseData;
      const treasuryPubkey = new PublicKey(treasuryWallet);
      const transferAmount = BigInt(amountUsdc) * BigInt(10 ** USDC_DECIMALS);

      // Check USDC balance before building tx
      setUsdcStatus('Checking USDC balance...');
      const balRes = await apiFetch(`${getApiHost()}/api/spotlight/usdc-balance/${publicKey.toBase58()}`);
      if (balRes.ok) {
        const balData = await balRes.json();
        if (balData.balance < amountUsdc) {
          setError(`Insufficient USDC balance. You have ${balData.balance} USDC but need ${amountUsdc} USDC.`);
          return;
        }
      }

      // Step 2: Build the USDC transfer transaction
      setUsdcStatus('Building transaction...');
      const senderAta = await getAssociatedTokenAddress(USDC_MINT, publicKey, false, TOKEN_PROGRAM_ID);
      const treasuryAta = await getAssociatedTokenAddress(USDC_MINT, treasuryPubkey, false, TOKEN_PROGRAM_ID);

      // Ensure treasury ATA exists (no-op if it does, creates if not)
      const createAtaIx = createAssociatedTokenAccountIdempotentInstruction(
        publicKey,          // payer
        treasuryAta,        // ata
        treasuryPubkey,     // owner
        USDC_MINT,          // mint
        TOKEN_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );

      const transferIx = createTransferCheckedInstruction(
        senderAta,
        USDC_MINT,
        treasuryAta,
        publicKey,
        transferAmount,
        USDC_DECIMALS,
        [],
        TOKEN_PROGRAM_ID
      );

      // Fetch blockhash via API proxy
      const blockhashRes = await apiFetch(`${getApiHost()}/api/burn/blockhash`);
      if (!blockhashRes.ok) throw new Error('Could not fetch blockhash');
      const { blockhash } = await blockhashRes.json();

      const transaction = new Transaction().add(createAtaIx, transferIx);
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      // Step 3: User signs
      setUsdcStatus('Approve the transfer in your wallet...');
      const signed = await signTransaction(transaction);

      // Step 4: Send via API proxy
      setUsdcStatus('Sending transaction...');
      const serialized = signed.serialize().toString('base64');
      const sendRes = await apiFetch(`${getApiHost()}/api/burn/send-transaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transaction: serialized }),
      });

      if (!sendRes.ok) {
        const errData = await sendRes.json();
        throw new Error(errData.error || 'Failed to send transaction');
      }
      const { signature } = await sendRes.json();

      // Step 5: Verify payment on the API
      setUsdcStatus('Verifying payment...');
      const verifyRes = await apiFetch(`${getApiHost()}/api/spotlight/verify-usdc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ spotlightId, txSignature: signature }),
      });

      if (!verifyRes.ok) {
        const errData = await verifyRes.json();
        throw new Error(errData.error || 'Verification failed');
      }

      setUsdcSuccess({ txSignature: signature });
      setUsdcStatus(null);
    } catch (err: any) {
      if (err.message?.includes('User rejected') || err.message?.includes('rejected the request')) {
        setError('Transaction cancelled');
      } else {
        setError(err.message || 'USDC payment failed');
      }
      setUsdcStatus(null);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getAvailability = (tier: 'regular' | 'sponsor') => {
    const avail = tier === 'regular' ? regularAvailability : sponsorAvailability;
    if (!avail) return null;
    if (avail.slotsAvailable > 0) {
      return (
        <span className="text-crypto-accent text-xs">
          {avail.slotsAvailable} slot{avail.slotsAvailable > 1 ? 's' : ''} available now
        </span>
      );
    }
    const next = new Date(avail.nextAvailableAt);
    const hoursUntil = Math.max(0, Math.ceil((next.getTime() - Date.now()) / (1000 * 60 * 60)));
    return (
      <span className="text-crypto-yellow text-xs">
        Next slot in ~{hoursUntil}h (queued)
      </span>
    );
  };

  const formatMarketCap = (mc: number): string => {
    if (!mc || mc <= 0) return '';
    if (mc < 1_000_000) return `$${(mc / 1000).toFixed(1)}K`;
    if (mc < 1_000_000_000) return `$${(mc / 1_000_000).toFixed(1)}M`;
    return `$${(mc / 1_000_000_000).toFixed(1)}B`;
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-crypto-gray border border-gray-700 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white">Spotlight a Token</h2>
            <p className="text-sm text-gray-400 mt-1">Feature a clean watchlist token at the top of the page. If a subsequent scan flags risks, the spotlight will be removed with no refund.</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1 ml-4 flex-shrink-0"
          >
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Token Input */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Token Mint Address</label>
            {preselectedMint ? (
              <div className="bg-crypto-dark border border-gray-600 rounded-lg p-3 flex items-center gap-2">
                <span className="font-semibold text-white">{preselectedSymbol || ''}</span>
                <span className="text-gray-400 text-sm truncate">{preselectedName || preselectedMint}</span>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={mintInput}
                  onChange={e => setMintInput(e.target.value.trim())}
                  placeholder="Paste token mint address..."
                  className={`w-full bg-crypto-dark border rounded-lg p-3 text-white font-mono text-sm placeholder-gray-500 focus:outline-none ${
                    validatedToken
                      ? 'border-crypto-accent/50 focus:border-crypto-accent'
                      : validationError
                        ? 'border-red-500/50 focus:border-red-500'
                        : 'border-gray-600 focus:border-crypto-accent'
                  }`}
                />
                {validating && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <i className="fas fa-spinner fa-spin text-gray-400 text-sm"></i>
                  </div>
                )}
              </div>
            )}
            {!preselectedMint && !validatedToken && !validationError && (
              <p className="text-gray-500 text-xs mt-1.5">Must be a clean token on the watchlist with research in the last hour</p>
            )}
            {validationError && (
              <p className="text-red-400 text-xs mt-1.5"><i className="fas fa-exclamation-circle mr-1"></i>{validationError}</p>
            )}
          </div>

          {/* Token Preview — shown when validated */}
          {validatedToken && (
            <SpotlightPreviewCard
              token={validatedToken}
              isSponsor={selectedOption?.tier === 'sponsor'}
              formatTimeAgo={formatTimeAgo}
              formatMarketCap={formatMarketCap}
            />
          )}

          {/* Spotlight Options — only show after token is validated */}
          {(validatedToken || preselectedMint) && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Choose a Plan</label>
              <div className="space-y-3">
                {OPTIONS.map(option => {
                  const isSelected = selectedOption?.tier === option.tier && selectedOption?.durationHours === option.durationHours;
                  const isSponsor = option.tier === 'sponsor';

                  return (
                    <button
                      key={`${option.tier}_${option.durationHours}`}
                      onClick={() => setSelectedOption(option)}
                      className={`w-full text-left p-4 rounded-xl border transition-all ${
                        isSelected
                          ? isSponsor
                            ? 'border-purple-500 bg-purple-500/10'
                            : 'border-crypto-accent bg-crypto-accent/10'
                          : 'border-gray-700 bg-crypto-dark hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          {isSponsor && (
                            <i className="fas fa-crown text-purple-400 text-sm"></i>
                          )}
                          <span className={`font-semibold ${isSponsor ? 'text-purple-300' : 'text-white'}`}>
                            {option.label}
                          </span>
                        </div>
                        {isSelected && (
                          <span className="font-bold text-lg text-white">{option.price}</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-400">{option.description}</p>
                      </div>
                      <div className="mt-2">
                        {getAvailability(option.tier)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Payment Method Toggle — only show after token is validated and plan selected */}
          {(validatedToken || preselectedMint) && selectedOption && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Payment Method</label>
              <div className="flex rounded-xl overflow-hidden border border-gray-700">
                <button
                  onClick={() => { setPaymentMethod('usdc'); setError(null); }}
                  className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    paymentMethod === 'usdc'
                      ? 'bg-blue-500/15 text-blue-400 border-r border-blue-500/30'
                      : 'bg-crypto-dark text-gray-400 hover:text-gray-300 border-r border-gray-700'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="16" r="16" fill="currentColor" opacity="0.2"/><path d="M20.8 18.4c0-2.4-1.6-3.2-4.8-3.6-2.4-.4-2.8-.8-2.8-1.6s.8-1.2 2-1.2c1.2 0 2 .4 2.4 1.2l2.4-1.2c-.8-1.2-2-2-3.6-2.4V8h-2.4v1.6c-2.4.4-3.6 2-3.6 3.6 0 2.4 1.6 3.2 4.8 3.6 2 .4 2.8.8 2.8 1.6s-1.2 1.6-2.4 1.6c-1.6 0-2.4-.8-2.8-1.6l-2.4 1.2c.8 1.6 2 2.4 4 2.8V24h2.4v-1.6c2.4-.4 4-1.6 4-4z" fill="currentColor"/></svg>
                  USDC
                </button>
                <button
                  onClick={() => { setPaymentMethod('card'); setUsdcSuccess(null); setError(null); }}
                  className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    paymentMethod === 'card'
                      ? 'bg-crypto-accent/15 text-crypto-accent'
                      : 'bg-crypto-dark text-gray-400 hover:text-gray-300'
                  }`}
                >
                  <i className="fas fa-credit-card text-xs"></i>
                  Card
                </button>
              </div>
              {paymentMethod === 'usdc' && !connected && (
                <p className="text-gray-500 text-xs mt-2">
                  <i className="fas fa-wallet mr-1"></i>
                  You'll need to connect your wallet to pay with USDC
                </p>
              )}
            </div>
          )}

          {/* Email (optional) — only show for card payments after token is validated */}
          {(validatedToken || preselectedMint) && paymentMethod === 'card' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email <span className="text-gray-500">(optional, for receipt)</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-crypto-dark border border-gray-600 rounded-lg p-3 text-white placeholder-gray-500 focus:border-crypto-accent focus:outline-none"
              />
            </div>
          )}

          {/* USDC Success */}
          {usdcSuccess && (
            <div className="bg-crypto-accent/10 border border-crypto-accent/30 rounded-lg p-3">
              <p className="text-crypto-accent text-sm font-medium">
                Payment confirmed! Your spotlight is now {selectedOption ? (selectedOption.tier === 'sponsor' ? 'live as Research Sponsor' : 'live') : 'active'}.
              </p>
              <a
                href={`https://solscan.io/tx/${usdcSuccess.txSignature}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white text-xs mt-1 block transition-colors"
              >
                View transaction on Solscan →
              </a>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Purchase Button — only show after token is validated, hide after USDC success */}
          {(validatedToken || preselectedMint) && !usdcSuccess && (
            <button
              onClick={handlePurchase}
              disabled={(!validatedToken && !preselectedMint) || !selectedOption || loading}
              className={`w-full py-3 rounded-xl font-semibold text-lg transition-all ${
                (!validatedToken && !preselectedMint) || !selectedOption || loading
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : paymentMethod === 'usdc'
                    ? 'bg-blue-600 hover:bg-blue-500 text-white'
                    : selectedOption?.tier === 'sponsor'
                      ? 'bg-purple-600 hover:bg-purple-500 text-white'
                      : 'bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark'
              }`}
            >
              {loading ? (
                <span><i className="fas fa-spinner fa-spin mr-2"></i>{usdcStatus || 'Processing...'}</span>
              ) : selectedOption ? (
                paymentMethod === 'usdc'
                  ? `Pay ${selectedOption.priceUsdc} USDC — ${selectedOption.label}`
                  : `Pay ${selectedOption.price} — ${selectedOption.label}`
              ) : (
                'Select a plan'
              )}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SpotlightPurchaseModal;
